import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { estadisticasAPI, iniciativasAPI, pedidosAPI, hitosFacturacionAPI, objetivosFacturacionAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList,
} from 'recharts';

// ─── Color Palette ─────────────────────────────────────────────────────────────
const DOMAIN = {
  oportunidades: { primary: '#4878d0', soft: '#eff6ff', mid: '#6ea5f7', glow: 'rgba(72,120,208,0.06)' },
  operaciones:   { primary: '#2d9368', soft: '#ecfdf5', mid: '#3dc896', glow: 'rgba(45,147,104,0.06)'  },
  innovacion:    { primary: '#8654cc', soft: '#f5f3ff', mid: '#a47ef4', glow: 'rgba(134,84,204,0.06)'},
};

const STATE_C = {
  oportunidades: {
    identificadas:      '#aab6c7',
    en_propuesta:       '#6ea5f7',
    validacion_tecnica: '#f7b73a',
    pdte_aprobacion:    '#faa168',
    aprobadas:          '#3dc896',
    rechazadas:         '#faa0a0',
    stand_by:           '#e2e8f0',
  },
  operaciones: {
    por_planificar:          '#aab6c7',
    planificadas:            '#87bcfb',
    en_curso_no_desviadas:   '#3dc896',
    en_curso_desviadas:      '#f7b73a',
    bloqueadas:              '#faa0a0',
    finalizadas:             '#858c97',
    canceladas:              '#d1d5db',
  },
  innovacion: {
    innocards_ok:            '#9660ea',
    innocards_desviadas:     '#f7b73a',
    innocards_finalizadas:   '#cfc8fd',
    innotrans_ok:            '#bc7af8',
    innotrans_desviadas:     '#faa168',
    innotrans_finalizadas:   '#e5e0fe',
  },
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) => {
  if (!n) return '—';
  const v = parseFloat(n);
  if (v === 0) return '—';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M€`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(0)}K€`;
  return `${Math.round(v)}€`;
};

const pct = (part, total) => total > 0 ? Math.round((part / total) * 100) : 0;

// ─── Sub-components ────────────────────────────────────────────────────────────

/** Animated number: counts up on mount */
const AnimatedNum = ({ value, duration = 800 }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!value) return;
    let start = 0;
    const end = parseInt(value, 10);
    if (end === 0) return;
    const step = Math.ceil(end / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setDisplay(end); clearInterval(timer); }
      else setDisplay(start);
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <span>{display}</span>;
};

/** Probability pills shown under the Entregada row */
const ProbPills = ({ alta = 0, media = 0, baja = 0, sinProb = 0 }) => (
  <div style={{ display: 'flex', gap: 5, paddingLeft: 15, paddingBottom: 3 }}>
    {[['Alta', alta, '#3dc896'], ['Media', media, '#f7b73a'], ['Baja', baja, '#faa0a0'],
      ['—', sinProb, '#d1d5db']].map(([label, val, color]) =>
      val > 0 ? (
        <div key={label} style={{
          display: 'flex', alignItems: 'center', gap: 3,
          padding: '1px 7px', borderRadius: 10,
          backgroundColor: color + '18', border: `1px solid ${color}44`,
          fontSize: '0.65rem', fontWeight: '700', color,
          whiteSpace: 'nowrap',
        }}>
          <span style={{ fontSize: '0.55rem', opacity: 0.8 }}>{label}</span>
          <span>{val}</span>
        </div>
      ) : null
    )}
  </div>
);

/** En-curso grouped row: stacked tri-color bar + sub-pills (OK / Medio / Grave) */
const EnCursoRow = ({ ok, medio, grave, costTotal, total, onClick }) => {
  const totalEC = ok + medio + grave;
  const w = (n) => pct(n, total);
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '4px 0', opacity: totalEC === 0 ? 0.38 : 1,
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: 4, transition: 'background 0.15s',
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.background = '#f8fafc'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#3dc896', flexShrink: 0 }} />
      {/* Label + inline mini colored counts */}
      <div style={{ width: '115px', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, overflow: 'hidden' }}>
        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#111827', whiteSpace: 'nowrap' }}>En curso</span>
        <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
          {ok > 0    && <span style={{ fontSize: '0.62rem', fontWeight: '800', color: '#3dc896' }}>{ok}</span>}
          {medio > 0 && <span style={{ fontSize: '0.62rem', fontWeight: '800', color: '#f7b73a' }}>{medio}</span>}
          {grave > 0 && <span style={{ fontSize: '0.62rem', fontWeight: '800', color: '#f47070' }}>{grave}</span>}
        </div>
      </div>
      {/* Stacked tri-color bar */}
      <div style={{ flex: 1, height: 3, backgroundColor: '#f1f5f9', borderRadius: 2, overflow: 'hidden', display: 'flex' }}>
        <div style={{ width: `${w(ok)}%`,    backgroundColor: '#3dc896', height: '100%', transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)' }} />
        <div style={{ width: `${w(medio)}%`, backgroundColor: '#f7b73a', height: '100%', transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)' }} />
        <div style={{ width: `${w(grave)}%`, backgroundColor: '#f47070', height: '100%', transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)' }} />
      </div>
      <span style={{ width: 24, textAlign: 'right', fontSize: '0.82rem', fontWeight: '700', color: '#111827' }}>{totalEC}</span>
      <span style={{ width: 64, textAlign: 'right', fontSize: '0.72rem', color: '#6b7280', fontWeight: '500' }}>{fmt(costTotal)}</span>
    </div>
  );
};

/** State row: label · bar · count · cost */
const StateRow = ({ label, count, cost, color, total, highlight, onClick }) => {
  const w = pct(count, total);
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '4px 0', opacity: count === 0 ? 0.38 : 1,
        transition: 'opacity 0.2s, background 0.15s',
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: 4,
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.background = '#f8fafc'; }}
      onMouseLeave={e => { if (onClick) e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
      <span style={{
        width: '115px', fontSize: '0.75rem',
        color: highlight ? '#111827' : '#4b5563',
        fontWeight: highlight ? '600' : '400',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {label}
      </span>
      {/* bar */}
      <div style={{ flex: 1, height: 3, backgroundColor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${w}%`, backgroundColor: color, borderRadius: 2, transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)' }} />
      </div>
      <span style={{ width: 24, textAlign: 'right', fontSize: '0.82rem', fontWeight: '700', color: '#111827' }}>
        {count}
      </span>
      {cost !== undefined && (
        <span style={{ width: 64, textAlign: 'right', fontSize: '0.72rem', color: '#6b7280', fontWeight: '500' }}>
          {fmt(cost)}
        </span>
      )}
    </div>
  );
};

/** Mini donut chart */
const MiniDonut = ({ data, colors, size = 112, centerLabel, pctLabels, onSliceClick }) => {
  const renderPctLabel = ({ cx, cy, midAngle, outerRadius, percent, index }) => {
    if (percent < 0.06) return null;
    const RADIAN = Math.PI / 180;
    const r = outerRadius + 11;
    const x = cx + r * Math.cos(-midAngle * RADIAN);
    const y = cy + r * Math.sin(-midAngle * RADIAN);
    const color = colors[index % colors.length];
    return (
      <text x={x} y={y} textAnchor="middle" dominantBaseline="central"
        fill={color} style={{ fontSize: '0.52rem', fontWeight: '700' }}>
        {`${Math.round(percent * 100)}%`}
      </text>
    );
  };
  const hasData = data && data.some(d => d.value > 0);
  if (!hasData) {
    return (
      <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>Sin datos</span>
        </div>
      </div>
    );
  }
  return (
    <div style={{ position: 'relative', overflow: 'visible' }}>
      <ResponsiveContainer width={size} height={size} style={{ overflow: 'visible' }}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%"
            innerRadius={pctLabels ? size * 0.26 : size * 0.29}
            outerRadius={pctLabels ? size * 0.40 : size * 0.46}
            dataKey="value" strokeWidth={2} stroke="white" paddingAngle={2}
            label={pctLabels ? renderPctLabel : undefined} labelLine={false}
            onClick={onSliceClick ? (data, index, event) => { if (event) event.stopPropagation(); onSliceClick(data, index); } : undefined}
            style={{ cursor: onSliceClick ? 'pointer' : 'default' }}>
            {data.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
          </Pie>
          <Tooltip
            formatter={(v, n) => [v, n]}
            contentStyle={{ fontSize: '0.7rem', borderRadius: '6px', border: '1px solid #e5e7eb', padding: '4px 8px' }}
          />
        </PieChart>
      </ResponsiveContainer>
      {centerLabel && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          textAlign: 'center', pointerEvents: 'none',
        }}>
          <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#111827', lineHeight: 1 }}>{centerLabel.value}</div>
          <div style={{ fontSize: '0.55rem', color: '#9ca3af', marginTop: 1 }}>{centerLabel.label}</div>
        </div>
      )}
    </div>
  );
};

/** Small KPI badge at bottom of panel */
const FooterKpi = ({ value, label, color, subValue, pills, labelTop, onClick, costePair }) => (
  <div
    onClick={onClick}
    style={{
      flex: 1, textAlign: 'center', padding: '8px 10px',
      backgroundColor: color + '12', borderRadius: 8,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'filter 0.15s',
    }}
    onMouseEnter={e => { if (onClick) e.currentTarget.style.filter = 'brightness(0.95)'; }}
    onMouseLeave={e => { if (onClick) e.currentTarget.style.filter = 'none'; }}
  >
    {labelTop && (
      <div style={{ fontSize: '0.63rem', color: '#6b7280', marginBottom: 4 }}>{label}</div>
    )}
    <div style={{ fontSize: '1.15rem', fontWeight: '900', color, lineHeight: 1 }}>{value}</div>
    {subValue && (
      <div style={{ fontSize: '0.68rem', fontWeight: '600', color, opacity: 0.75, marginTop: 2, lineHeight: 1 }}>{subValue}</div>
    )}
    {costePair && (() => {
      const overrun = costePair.real > costePair.venta && costePair.venta > 0;
      return (
        <div
          title={`Venta: ${costePair.venta > 0 ? fmt(costePair.venta) : '—'}  |  Consumido: ${costePair.real > 0 ? fmt(costePair.real) : '—'}`}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, marginTop: 4 }}
        >
          <span style={{ fontSize: '0.68rem', fontWeight: '800', color }}>
            {costePair.venta > 0 ? fmt(costePair.venta) : '—'}
          </span>
          <span style={{ fontSize: '0.55rem', color: '#d1d5db' }}>/</span>
          <span style={{ fontSize: '0.6rem', fontWeight: '400', color: overrun ? '#f47070' : color, opacity: overrun ? 1 : 0.55 }}>
            {costePair.real > 0 ? fmt(costePair.real) : '—'}
          </span>
        </div>
      );
    })()}
    {pills && (
      <div style={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', marginTop: 5 }}>
        {pills.map(({ label: l, value: v, color: c }) => v > 0 ? (
          <div key={l} style={{
            display: 'flex', alignItems: 'center', gap: 2,
            padding: '1px 5px', borderRadius: 8,
            backgroundColor: c + '22', border: `1px solid ${c}55`,
            fontSize: '0.6rem', fontWeight: '700', color: c,
          }}>
            <span style={{ opacity: 0.8, fontSize: '0.55rem' }}>{l}</span>
            <span>{v}</span>
          </div>
        ) : null)}
      </div>
    )}
    {!labelTop && (
      <div style={{ fontSize: '0.63rem', color: '#6b7280', marginTop: 3 }}>{label}</div>
    )}
  </div>
);

/** Panel wrapper card */
const Panel = ({ children, domain, onClick }) => (
  <div
    onClick={onClick}
    style={{
      backgroundColor: 'white', borderRadius: 14,
      border: `1px solid ${domain.primary}22`,
      boxShadow: `0 1px 3px rgba(0,0,0,0.04), 0 6px 20px ${domain.glow}`,
      overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'box-shadow 0.2s, transform 0.15s',
      display: 'flex', flexDirection: 'column',
    }}
    onMouseEnter={e => { if (onClick) { e.currentTarget.style.boxShadow = `0 4px 16px rgba(0,0,0,0.08), 0 8px 24px ${domain.glow}`; e.currentTarget.style.transform = 'translateY(-1px)'; }}}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = `0 1px 3px rgba(0,0,0,0.04), 0 6px 20px ${domain.glow}`; e.currentTarget.style.transform = 'translateY(0)'; }}
  >
    {children}
  </div>
);

/** Panel header with colored top accent */
const PanelHeader = ({ icon, title, subtitle, total, domain, badge, hideTotal, rightContent }) => (
  <div style={{ borderTop: `3px solid ${domain.primary}` }}>
    <div style={{ padding: '18px 20px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            backgroundColor: domain.soft,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.05rem',
          }}>
            {icon}
          </div>
          <div>
            <div style={{ fontSize: '0.65rem', fontWeight: '600', color: domain.mid, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {subtitle}
            </div>
            <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#111827', lineHeight: 1.2 }}>
              {title}
            </div>
          </div>
        </div>
        {badge && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 8px', borderRadius: 20,
            backgroundColor: domain.soft, fontSize: '0.7rem',
            color: domain.primary, fontWeight: '600', marginTop: 4,
          }}>
            {badge}
          </div>
        )}
      </div>
      {rightContent
        ? <div onClick={e => e.stopPropagation()}>{rightContent}</div>
        : (!hideTotal && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '2.6rem', fontWeight: '900', color: domain.primary, lineHeight: 1, letterSpacing: '-0.03em' }}>
              <AnimatedNum value={total} />
            </div>
            <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: 2 }}>Total</div>
          </div>
        ))
      }
    </div>
  </div>
);

// ─── Area Hub Block ───────────────────────────────────────────────────────────
const AREA_COLORS = ['#3bbcee','#2e9ad4','#2c88b8','#2e7aa0','#2c6c88','#2e6d7e','#3c5e80','#3a556e'];

const AreaMetricBar = ({ segments, maxVal }) => {
  const pctOf = (v) => maxVal > 0 ? Math.max(0, Math.min(100, (v / maxVal) * 100)) : 0;
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  return (
    <div style={{ flex: 1, height: 8, backgroundColor: '#f1f5f9', borderRadius: 4, overflow: 'hidden', display: 'flex' }}>
      {segments.map((seg, i) => (
        seg.value > 0 ? (
          <div key={i} title={`${seg.label}: ${seg.value}`} style={{
            width: `${pctOf(seg.value)}%`, height: '100%',
            backgroundColor: seg.color, flexShrink: 0,
            transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)',
          }} />
        ) : null
      ))}
      {total === 0 && (
        <div style={{ width: '4px', height: '100%', backgroundColor: '#e2e8f0', borderRadius: 4 }} />
      )}
    </div>
  );
};

const AreaHubBlock = ({ data }) => {
  const [metric, setMetric] = React.useState('iniciativas');

  if (!data?.areas?.length) return null;
  const areas = data.areas;

  const maxTotal  = Math.max(...areas.map(a => a.total), 1);
  const maxVentas = Math.max(...areas.map(a => a.importe_cliente), 1);
  const maxCostes = Math.max(...areas.map(a => a.coste_estimado), 1);

  const maxEconomico = Math.max(maxVentas, maxCostes);

  const getSegments = (area) => {
    if (metric === 'iniciativas') return [
      { value: area.oportunidades,       color: '#6ea5f7', label: 'En prep. / Entregadas'            },
      { value: area.operaciones_activas, color: '#3dc896', label: 'Ops. activas (pte/plan/en curso)'  },
      { value: area.innovacion,          color: '#a47ef4', label: 'Innov. activa (pte/plan/en curso)' },
      { value: area.finalizadas,         color: '#aab6c7', label: 'Finalizadas'                       },
    ];
    return [];
  };

  const maxVal = metric === 'iniciativas' ? maxTotal : maxEconomico;

  const totalOportunidades     = areas.reduce((s, a) => s + (a.oportunidades       || 0), 0);
  const totalOperacionesActivas = areas.reduce((s, a) => s + (a.operaciones_activas || 0), 0);
  const totalInnovacion         = areas.reduce((s, a) => s + (a.innovacion          || 0), 0);
  const totalFinalizadas        = areas.reduce((s, a) => s + (a.finalizadas         || 0), 0);

  const legend = metric === 'iniciativas'
    ? [
        { color: '#6ea5f7', label: 'En prep. / Entregadas',   total: totalOportunidades      },
        { color: '#3dc896', label: 'Operaciones activas',      total: totalOperacionesActivas },
        { color: '#a47ef4', label: 'Innovación activa',       total: totalInnovacion         },
        { color: '#aab6c7', label: 'Finalizadas',              total: totalFinalizadas        },
      ]
    : [];

  const metricTabs = [
    { key: 'iniciativas', label: 'Iniciativas'     },
    { key: 'economico',   label: 'Ventas / Costes' },
  ];

  const rightLabel = metric === 'iniciativas' ? 'Total' : '';

  return (
    <div style={{
      backgroundColor: 'white', borderRadius: 14,
      border: '1px solid #d0edf8',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 6px 20px rgba(46,154,212,0.05)',
      overflow: 'hidden', marginTop: 20,
    }}>
      {/* ── Header ── */}
      <div style={{ borderTop: '3px solid #2e9ad4' }}>
        <div style={{ padding: '16px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, backgroundColor: '#d6edf8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.05rem' }}>
              🏢
            </div>
            <div>
              <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#2e9ad4', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Visión Global
              </div>
              <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#111827', lineHeight: 1.2 }}>
                Estado por Área de Conocimiento
              </div>
            </div>
            {legend.length > 0 && (
              <div style={{ display: 'flex', gap: 10, marginLeft: 6, flexWrap: 'wrap' }}>
                {legend.map(l => (
                  <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: l.color }} />
                    <span style={{ fontSize: '0.63rem', color: '#6b7280' }}>{l.label}</span>
                    <span style={{
                      fontSize: '0.63rem', fontWeight: '700', color: l.color,
                      backgroundColor: l.color + '18',
                      padding: '0px 5px', borderRadius: 8,
                    }}>{l.total}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Metric tabs */}
          <div style={{ display: 'flex', gap: 3, padding: 3, backgroundColor: '#f1f5f9', borderRadius: 8 }}>
            {metricTabs.map(({ key, label }) => (
              <button key={key}
                onClick={() => setMetric(key)}
                style={{
                  padding: '4px 12px', fontSize: '0.68rem',
                  fontWeight: metric === key ? '700' : '500',
                  color: metric === key ? '#2e9ad4' : '#6b7280',
                  backgroundColor: metric === key ? 'white' : 'transparent',
                  border: 'none', borderRadius: 6, cursor: 'pointer',
                  boxShadow: metric === key ? '0 1px 3px rgba(0,0,0,0.10)' : 'none',
                  transition: 'all 0.15s',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Area rows — single column, full width ── */}
      <div style={{ padding: '0 20px 20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 4, borderBottom: '1px solid #f1f5f9', marginBottom: 2 }}>
          <div style={{ width: 7, flexShrink: 0 }} />
          <span style={{ width: 160, fontSize: '0.62rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>Área</span>
          <div style={{ flex: 1 }} />
          <span style={{ width: 36, textAlign: 'right', fontSize: '0.62rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase' }}>
            {rightLabel}
          </span>
          {metric === 'economico' && (
            <>
              <span style={{ width: 90, textAlign: 'right', fontSize: '0.62rem', color: '#28a87b', fontWeight: '600', textTransform: 'uppercase' }}>Ventas</span>
              <span style={{ width: 90, textAlign: 'right', fontSize: '0.62rem', color: '#e09c30', fontWeight: '600', textTransform: 'uppercase' }}>Costes</span>
            </>
          )}
          {metric === 'iniciativas' && (
            <>
              <span title="En preparación / Entregadas" style={{ width: 60, textAlign: 'right', fontSize: '0.62rem', color: '#6ea5f7', fontWeight: '600', textTransform: 'uppercase' }}>Prep.</span>
              <span title="Iniciativas/Mant./Servicios: por planificar, planificadas o en curso" style={{ width: 60, textAlign: 'right', fontSize: '0.62rem', color: '#3dc896', fontWeight: '600', textTransform: 'uppercase' }}>Ops.</span>
              <span title="Finalizadas (todo tipo)" style={{ width: 60, textAlign: 'right', fontSize: '0.62rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Fin.</span>
              <span title="Innocards/Innotrans: por planificar, planificadas o en curso" style={{ width: 60, textAlign: 'right', fontSize: '0.62rem', color: '#a47ef4', fontWeight: '600', textTransform: 'uppercase' }}>Innov.</span>
            </>
          )}
        </div>

        {areas.map((area, idx) => {
          const segments = getSegments(area);
          const mainVal  = metric === 'iniciativas' ? area.total : 0;
          const dotColor = AREA_COLORS[idx % AREA_COLORS.length];

          return (
            <div key={area.area} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f8fafc' }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0 }} />
              <span style={{
                width: 160, fontSize: '0.76rem', fontWeight: '600', color: '#1e293b',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0,
              }} title={area.area}>
                {area.area}
              </span>
              {/* Bar area */}
              {metric === 'iniciativas' ? (
                <AreaMetricBar segments={segments} maxVal={maxVal} />
              ) : (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, justifyContent: 'center' }}>
                  <div style={{ height: 6, backgroundColor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}
                       title={`Ventas: ${fmt(area.importe_cliente)}`}>
                    <div style={{
                      height: '100%',
                      width: `${maxEconomico > 0 ? Math.min(100, (area.importe_cliente / maxEconomico) * 100) : 0}%`,
                      backgroundColor: '#28a87b', borderRadius: 3,
                      transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)',
                    }} />
                  </div>
                  <div style={{ height: 6, backgroundColor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}
                       title={`Costes: ${fmt(area.coste_estimado)}`}>
                    <div style={{
                      height: '100%',
                      width: `${maxEconomico > 0 ? Math.min(100, (area.coste_estimado / maxEconomico) * 100) : 0}%`,
                      backgroundColor: '#f7b73a', borderRadius: 3,
                      transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)',
                    }} />
                  </div>
                </div>
              )}
              {/* Right side */}
              <span style={{ width: 36, textAlign: 'right', fontSize: '0.85rem', fontWeight: '700', color: '#111827', flexShrink: 0 }}>
                {metric === 'iniciativas' ? mainVal : ''}
              </span>
              {metric === 'economico' && (
                <>
                  <span style={{ width: 90, textAlign: 'right', fontSize: '0.75rem', color: '#28a87b', fontWeight: '600', flexShrink: 0 }}>
                    {area.importe_cliente > 0 ? fmt(area.importe_cliente) : '—'}
                  </span>
                  <span style={{ width: 90, textAlign: 'right', fontSize: '0.75rem', color: '#e09c30', fontWeight: '600', flexShrink: 0 }}>
                    {area.coste_estimado > 0 ? fmt(area.coste_estimado) : '—'}
                  </span>
                </>
              )}
              {metric === 'iniciativas' && (
                <>
                  <span style={{ width: 60, textAlign: 'right', fontSize: '0.75rem', fontWeight: area.oportunidades > 0 ? '700' : '400', color: area.oportunidades > 0 ? '#6ea5f7' : '#d1d5db', flexShrink: 0 }}>
                    {area.oportunidades}
                  </span>
                  <span style={{ width: 60, textAlign: 'right', fontSize: '0.75rem', fontWeight: area.operaciones_activas > 0 ? '700' : '400', color: area.operaciones_activas > 0 ? '#3dc896' : '#d1d5db', flexShrink: 0 }}>
                    {area.operaciones_activas}
                  </span>
                  <span style={{ width: 60, textAlign: 'right', fontSize: '0.75rem', fontWeight: area.finalizadas > 0 ? '700' : '400', color: area.finalizadas > 0 ? '#6b7280' : '#d1d5db', flexShrink: 0 }}>
                    {area.finalizadas}
                  </span>
                  <span style={{ width: 60, textAlign: 'right', fontSize: '0.75rem', fontWeight: area.innovacion > 0 ? '700' : '400', color: area.innovacion > 0 ? '#a47ef4' : '#d1d5db', flexShrink: 0 }}>
                    {area.innovacion}
                  </span>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Presupuesto Block ────────────────────────────────────────────────────────
const DOMAIN_PRESUPUESTO = { primary: '#c87828', soft: '#fffbeb', mid: '#e09c30', glow: 'rgba(200,120,40,0.06)' };
const GaugeBar = ({ pct, color, height = 5 }) => (
  <div style={{ flex: 1, height, backgroundColor: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
    <div style={{
      height: '100%',
      width: `${Math.min(pct, 100)}%`,
      backgroundColor: color,
      borderRadius: 4,
      transition: 'width 1s cubic-bezier(0.4,0,0.2,1)',
    }} />
  </div>
);

const PresupuestoBlock = ({ data }) => {
  if (!data) return null;
  const {
    total_presupuesto        = 0,
    total_consumido          = 0,
    total_comprometido       = 0,
    total_probabilidad_media = 0,
    total_probabilidad_baja  = 0,
    disponible_comprometido  = 0,
    por_estado               = {},
    desglose_tecnologia      = {},
  } = data;

  const pctConsumido    = total_presupuesto > 0 ? (total_consumido          / total_presupuesto) * 100 : 0;
  const pctComprometido = total_presupuesto > 0 ? (total_comprometido       / total_presupuesto) * 100 : 0;
  const pctProbMedia    = total_presupuesto > 0 ? (total_probabilidad_media / total_presupuesto) * 100 : 0;
  const pctProbBaja     = total_presupuesto > 0 ? (total_probabilidad_baja  / total_presupuesto) * 100 : 0;
  const disponible      = Math.max(disponible_comprometido, 0);

  const colorConsumido =
    pctConsumido >= 90 ? '#e05252' :
    pctConsumido >= 70 ? '#d97706' :
    '#3b82f6';

  const ESTADO_META = {
    recepcionado:       { color: '#3dc896', label: 'Recepcionado'    },
    aprobado:           { color: '#6ea5f7', label: 'Aprobado'        },
    comprometido:       { color: '#f7b73a', label: 'Comprometido'    },
    en_curso:           { color: '#8587f3', label: 'En curso'        },
    probabilidad_alta:  { color: '#9ad42a', label: 'Prob. alta'      },
    probabilidad_media: { color: '#fca668', label: 'Prob. media'     },
    probabilidad_baja:  { color: '#faa0a0', label: 'Prob. baja'      },
  };
  const totalPedidosImporte = Object.values(por_estado).reduce((s, v) => s + (v.total_importe || 0), 0);
  const totalPedidosCount   = Object.values(por_estado).reduce((s, v) => s + (v.count || 0), 0);

  const tecDataRaw = Object.entries(desglose_tecnologia).filter(([, v]) => v.consumido > 0 || v.por_consumir > 0);

  const donutPresupuesto = [
    { name: 'Consumido',     value: total_consumido,          color: colorConsumido },
    { name: 'Comprometido',  value: total_comprometido,       color: '#1565c0'      },
    { name: 'Prob. media',   value: total_probabilidad_media, color: '#f57f17'      },
    { name: 'Prob. baja',    value: total_probabilidad_baja,  color: '#c62828'      },
    { name: 'Disponible',    value: disponible,               color: '#e2e8f0'      },
  ].filter(d => d.value > 0);

  const donutEstado = Object.entries(ESTADO_META)
    .filter(([key]) => por_estado[key] && por_estado[key].count > 0)
    .map(([key, { color, label }]) => ({
      name: label,
      value: por_estado[key].total_importe,
      color,
      count: por_estado[key].count,
    }));

  const tecChartData = tecDataRaw.map(([, v]) => ({
    name: v.label,
    Consumido: v.consumido,
    'Por consumir': v.por_consumir,
  }));

  return (
    <div style={{
      backgroundColor: 'white', borderRadius: 14,
      border: `1px solid ${DOMAIN_PRESUPUESTO.primary}22`,
      boxShadow: `0 1px 3px rgba(0,0,0,0.04), 0 6px 20px ${DOMAIN_PRESUPUESTO.glow}`,
      overflow: 'hidden', marginTop: 20,
    }}>
      {/* ── Header ── */}
      <div style={{ borderTop: `3px solid ${DOMAIN_PRESUPUESTO.primary}` }}>
        <div style={{ padding: '16px 20px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, backgroundColor: DOMAIN_PRESUPUESTO.soft, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.05rem' }}>
              💰
            </div>
            <div>
              <div style={{ fontSize: '0.65rem', fontWeight: '600', color: DOMAIN_PRESUPUESTO.mid, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Control Económico
              </div>
              <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#111827', lineHeight: 1.2 }}>
                Presupuesto &amp; Pedidos
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 20,
              backgroundColor: DOMAIN_PRESUPUESTO.soft,
              fontSize: '0.72rem', fontWeight: '700', color: DOMAIN_PRESUPUESTO.primary,
            }}>
              Total: {fmt(total_presupuesto)}
            </div>
            {totalPedidosCount > 0 && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 10px', borderRadius: 20,
                backgroundColor: '#eff6ff',
                fontSize: '0.72rem', fontWeight: '700', color: '#3b82f6',
              }}>
                {totalPedidosCount} pedidos
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ padding: '0 20px 20px', display: 'flex', gap: 20, flexWrap: 'wrap' }}>

        {/* ── Left: gauges + donut ── */}
        <div style={{ minWidth: 190, flex: '0 0 210px' }}>
          {/* Donut */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
            <div style={{ position: 'relative', overflow: 'visible' }}>
              <ResponsiveContainer width={160} height={160} style={{ overflow: 'visible' }}>
                <PieChart>
                  <Pie data={donutPresupuesto} cx="50%" cy="50%"
                    innerRadius={46} outerRadius={62}
                    dataKey="value" strokeWidth={2} stroke="white" paddingAngle={2}>
                    {donutPresupuesto.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: '0.7rem', borderRadius: 6, border: '1px solid #e5e7eb', padding: '4px 8px' }} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', pointerEvents: 'none' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: '900', color: colorConsumido, lineHeight: 1 }}>{pctConsumido.toFixed(0)}%</div>
                <div style={{ fontSize: '0.55rem', color: '#9ca3af', marginTop: 1 }}>consumido</div>
              </div>
            </div>
          </div>

          {/* KPI bars */}
          {[
            { label: 'Presupuesto total', value: fmt(total_presupuesto),        color: DOMAIN_PRESUPUESTO.primary, pct: 100           },
            { label: 'Consumido',        value: fmt(total_consumido),           color: colorConsumido,             pct: pctConsumido  },
            { label: 'Comprometido',     value: fmt(total_comprometido),        color: '#1565c0',                  pct: pctComprometido },
            { label: 'Prob. media',      value: fmt(total_probabilidad_media),  color: '#f57f17',                  pct: pctProbMedia  },
            { label: 'Prob. baja',       value: fmt(total_probabilidad_baja),   color: '#c62828',                  pct: pctProbBaja   },
            { label: 'Disponible',       value: fmt(disponible),                color: disponible_comprometido < 0 ? '#c62828' : '#00695c', pct: total_presupuesto > 0 ? (disponible / total_presupuesto) * 100 : 0 },
          ].map(({ label, value, color, pct: p }) => (
            <div key={label} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <span style={{ fontSize: '0.68rem', color: '#6b7280' }}>{label}</span>
                <span style={{ fontSize: '0.76rem', fontWeight: '700', color }}>{value}</span>
              </div>
              <GaugeBar pct={p} color={color} />
            </div>
          ))}
        </div>

        {/* ── Right: Estado de Pedidos + Por tecnología (stacked vertically) ── */}
        <div style={{ flex: '1 1 400px', minWidth: 300, borderLeft: '1px solid #f1f5f9', paddingLeft: 20 }}>

          {/* Estado de pedidos */}
          <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#374151', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Estado de pedidos
          </div>

          {donutEstado.length === 0 ? (
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center', paddingTop: 20 }}>Sin datos de pedidos</div>
          ) : (
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {/* Donut por estado */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <ResponsiveContainer width={140} height={140} style={{ overflow: 'visible' }}>
                  <PieChart>
                    <Pie data={donutEstado} cx="50%" cy="50%"
                      innerRadius={40} outerRadius={58}
                      dataKey="value" strokeWidth={2} stroke="white" paddingAngle={2}>
                      {donutEstado.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip
                      formatter={(v, name, props) => [fmt(v), props.payload.name]}
                      contentStyle={{ fontSize: '0.7rem', borderRadius: 6, border: '1px solid #e5e7eb', padding: '4px 8px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', pointerEvents: 'none' }}>
                  <div style={{ fontSize: '1.05rem', fontWeight: '900', color: '#111827', lineHeight: 1 }}>{totalPedidosCount}</div>
                  <div style={{ fontSize: '0.52rem', color: '#9ca3af', marginTop: 1 }}>pedidos</div>
                </div>
              </div>

              {/* Lista de estados */}
              <div style={{ flex: 1, minWidth: 130 }}>
                {Object.entries(ESTADO_META).map(([key, { color, label }]) => {
                  const e = por_estado[key];
                  if (!e || e.count === 0) return null;
                  const w = totalPedidosImporte > 0 ? (e.total_importe / totalPedidosImporte) * 100 : 0;
                  return (
                    <div key={key} style={{ marginBottom: 7 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                        <span style={{ flex: 1, fontSize: '0.69rem', color: '#374151', fontWeight: '600' }}>{label}</span>
                        <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#111827' }}>{e.count}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ flex: 1, height: 6, backgroundColor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${w}%`, backgroundColor: color, borderRadius: 3, transition: 'width 0.9s ease' }} />
                        </div>
                        <span style={{ width: 62, textAlign: 'right', fontSize: '0.67rem', color: '#6b7280', flexShrink: 0 }}>{fmt(e.total_importe)}</span>
                      </div>
                    </div>
                  );
                })}
                {/* Resumen total */}
                <div style={{ marginTop: 10, padding: '6px 10px', backgroundColor: DOMAIN_PRESUPUESTO.soft, borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.67rem', color: '#6b7280' }}>Total pedidos</span>
                  <span style={{ fontSize: '0.72rem', fontWeight: '700', color: DOMAIN_PRESUPUESTO.primary }}>{fmt(totalPedidosImporte)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Por tecnología */}
          {tecChartData.length > 0 && (
            <>
              <div style={{ height: 1, backgroundColor: '#f1f5f9', margin: '16px 0 12px' }} />
              <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#374151', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Por partida presupuestaria
              </div>
              <ResponsiveContainer width="100%" height={Math.max(80, tecChartData.length * 36 + 20)}>
                <BarChart layout="vertical" data={tecChartData} margin={{ top: 0, right: 10, left: 0, bottom: 0 }} barSize={14}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: '0.64rem', fill: '#4b5563' }} axisLine={false} tickLine={false} width={100} />
                  <Tooltip
                    formatter={(v, name) => [fmt(v), name]}
                    contentStyle={{ fontSize: '0.7rem', borderRadius: 6, border: '1px solid #e5e7eb', padding: '4px 8px' }}
                  />
                  <Bar dataKey="Consumido" stackId="a" fill={colorConsumido} />
                  <Bar dataKey="Por consumir" stackId="a" fill="#94a3b8" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                {[
                  { color: colorConsumido, label: 'Consumido'    },
                  { color: '#94a3b8',      label: 'Por consumir' },
                ].map(({ color, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: color }} />
                    <span style={{ fontSize: '0.62rem', color: '#6b7280' }}>{label}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

// ─── Facturación Histórico Block ──────────────────────────────────────────────
const DOMAIN_FACTURACION = { primary: '#299590', soft: '#f0fdfa', mid: '#3ccdc3', glow: 'rgba(41,149,144,0.06)' };
const FACTURACION_PALETTE = [
  '#2e9ad4', '#9660ea', '#e09c30', '#30b560', '#e05490',
  '#f07232', '#22a8c9', '#7572eb', '#7dbc1c', '#a85eed',
  '#c87828', '#2295b2', '#cc4278', '#2d9368', '#9848d4',
];

const FacturacionHistoricoBlock = ({ data, isManager }) => {
  const [view, setView] = React.useState('total');
  const [activeCategories, setActiveCategories] = React.useState(new Set());
  const [editingObjetivos, setEditingObjetivos] = React.useState(false);
  const [objetivosEdit, setObjetivosEdit] = React.useState({});
  const [savingObjetivos, setSavingObjetivos] = React.useState(false);
  const [objetivosData, setObjetivosData] = React.useState(null); // [{id, año, objetivo}]

  const toggleCategory = (cat) => {
    setActiveCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  if (!data || !data.años || data.años.length === 0) return null;

  const { años, por_año, pendiente_por_año, total_historico, total_pendiente, desglose } = data;
  const objetivos_por_año = data.objetivos_por_año || {};
  const cumplimiento_por_año = data.cumplimiento_por_año || {};

  const getChartConfig = () => {
    if (view === 'total') {
      return {
        chartData: años.map(año => ({
          año,
          'Facturado': por_año[año] || 0,
          'Objetivo': objetivos_por_año[año] || 0,
        })),
        categories: ['Facturado'],
      };
    }
    const dim = view === 'tipo' ? 'tipo' : view === 'negocio' ? 'negocio' : 'area';
    const desgloseMap = desglose[dim] || {};
    const categories = Object.keys(desgloseMap);
    const chartData = años.map(año => {
      const entry = { año, 'Objetivo': objetivos_por_año[año] || 0 };
      categories.forEach(cat => { entry[cat] = (desgloseMap[cat][año] || 0); });
      return entry;
    });
    return { chartData, categories };
  };

  const { chartData, categories } = getChartConfig();

  const visibleCategories = categories
    .map((cat, i) => ({ cat, origIdx: i }))
    .filter(({ cat }) => activeCategories.size === 0 || activeCategories.has(cat));

  const totalPerYearIndex = chartData.map(row =>
    visibleCategories.reduce((sum, { cat }) => sum + (row[cat] || 0), 0)
  );

  const bestYear = años.length > 0
    ? años.reduce((best, a) => (por_año[a] || 0) > (por_año[best] || 0) ? a : best, años[0])
    : null;

  const pendienteEntries = Object.entries(pendiente_por_año || {}).sort(([a], [b]) => Number(a) - Number(b));

  const viewTabs = [
    { key: 'total',   label: 'Total'       },
    { key: 'tipo',    label: 'Por tipo'    },
    { key: 'negocio', label: 'Por negocio' },
    { key: 'area',    label: 'Por área',   title: 'Área de ejecución' },
  ];

  const fmtY = (v) => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M€`;
    if (v >= 1_000)     return `${(v / 1_000).toFixed(0)}K€`;
    return `${v}€`;
  };

  const openEdit = async () => {
    try {
      const res = await objetivosFacturacionAPI.getAll();
      const list = res.data?.results ?? res.data;
      setObjetivosData(list);
      const map = {};
      list.forEach(o => { map[o.año] = { id: o.id, value: String(o.objetivo) }; });
      // Pre-fill missing years from chart
      años.forEach(a => { if (!map[a]) map[a] = { id: null, value: '' }; });
      setObjetivosEdit(map);
      setEditingObjetivos(true);
    } catch (e) {
      console.error('Error cargando objetivos', e);
    }
  };

  const saveObjetivos = async () => {
    setSavingObjetivos(true);
    try {
      for (const [añoStr, { id, value }] of Object.entries(objetivosEdit)) {
        const año = Number(añoStr);
        const objetivo = parseFloat(value.replace(',', '.'));
        if (!value || isNaN(objetivo)) continue;
        if (id) {
          await objetivosFacturacionAPI.update(id, { objetivo });
        } else {
          await objetivosFacturacionAPI.create({ año, objetivo });
        }
      }
      setEditingObjetivos(false);
      // Force page reload of historico data by dispatching a custom event
      window.dispatchEvent(new CustomEvent('objetivos-updated'));
    } catch (e) {
      console.error('Error guardando objetivos', e);
    } finally {
      setSavingObjetivos(false);
    }
  };

  return (
    <div style={{
      backgroundColor: 'white', borderRadius: 14,
      border: `1px solid ${DOMAIN_FACTURACION.primary}22`,
      boxShadow: `0 1px 3px rgba(0,0,0,0.04), 0 6px 20px ${DOMAIN_FACTURACION.glow}`,
      overflow: 'hidden', marginTop: 20,
    }}>
      {/* Header */}
      <div style={{ borderTop: `3px solid ${DOMAIN_FACTURACION.primary}` }}>
        <div style={{ padding: '16px 20px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, backgroundColor: DOMAIN_FACTURACION.soft, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.05rem' }}>📈</div>
            <div>
              <div style={{ fontSize: '0.65rem', fontWeight: '600', color: DOMAIN_FACTURACION.mid, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Control Económico</div>
              <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#111827', lineHeight: 1.2 }}>Histórico de Facturación</div>
            </div>
          </div>
          {/* Summary badges */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ padding: '3px 10px', borderRadius: 20, backgroundColor: DOMAIN_FACTURACION.soft, fontSize: '0.72rem', fontWeight: '700', color: DOMAIN_FACTURACION.primary }}>
              Total facturado: {fmt(total_historico)}
            </div>
            {total_pendiente > 0 && (
              <div style={{ padding: '3px 10px', borderRadius: 20, backgroundColor: '#fffbeb', border: '1px solid #f8d98844', fontSize: '0.72rem', fontWeight: '700', color: '#e09c30' }}>
                Pendiente: {fmt(total_pendiente)}
              </div>
            )}
            {/* Legend: objetivo bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', fontSize: '0.72rem', color: '#64748b', fontWeight: '600' }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: '#e2e8f0', border: '1.5px solid #94a3b8' }} />
              Objetivo
            </div>
            {isManager && (
              <button
                onClick={openEdit}
                style={{
                  padding: '3px 10px', borderRadius: 20, border: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc', fontSize: '0.72rem', fontWeight: '600',
                  color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                ✏️ Editar objetivos
              </button>
            )}
          </div>
          {/* View tabs */}
          <div style={{ display: 'flex', gap: 3, padding: 3, backgroundColor: '#f1f5f9', borderRadius: 8 }}>
            {viewTabs.map(({ key, label, title }) => (
              <button key={key} onClick={() => { setView(key); setActiveCategories(new Set()); }} title={title} style={{
                padding: '4px 12px', fontSize: '0.68rem',
                fontWeight: view === key ? '700' : '500',
                color: view === key ? DOMAIN_FACTURACION.primary : '#6b7280',
                backgroundColor: view === key ? 'white' : 'transparent',
                border: 'none', borderRadius: 6, cursor: 'pointer',
                boxShadow: view === key ? '0 1px 3px rgba(0,0,0,0.10)' : 'none',
                transition: 'all 0.15s',
              }}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Edit modal */}
      {editingObjetivos && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999,
          backgroundColor: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: 14, padding: 28,
            width: 360, boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          }}>
            <div style={{ fontSize: '1rem', fontWeight: '700', color: '#111827', marginBottom: 18 }}>
              Objetivos de Facturación Anual
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {años.sort((a, b) => a - b).map(año => (
                <div key={año} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ width: 40, fontSize: '0.82rem', fontWeight: '700', color: '#374151' }}>{año}</span>
                  <input
                    type="number"
                    min="0"
                    step="1000"
                    value={objetivosEdit[año]?.value ?? ''}
                    onChange={e => setObjetivosEdit(prev => ({
                      ...prev,
                      [año]: { ...(prev[año] || {}), value: e.target.value },
                    }))}
                    placeholder="Objetivo €"
                    style={{
                      flex: 1, padding: '6px 10px', borderRadius: 8,
                      border: '1px solid #d1d5db', fontSize: '0.82rem',
                      outline: 'none',
                    }}
                  />
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>€</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditingObjetivos(false)}
                style={{ padding: '7px 18px', borderRadius: 8, border: '1px solid #e5e7eb', backgroundColor: 'white', fontSize: '0.78rem', cursor: 'pointer', color: '#6b7280' }}
              >
                Cancelar
              </button>
              <button
                onClick={saveObjetivos}
                disabled={savingObjetivos}
                style={{
                  padding: '7px 18px', borderRadius: 8, border: 'none',
                  backgroundColor: DOMAIN_FACTURACION.primary, color: 'white',
                  fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer',
                  opacity: savingObjetivos ? 0.6 : 1,
                }}
              >
                {savingObjetivos ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ padding: '0 20px 20px' }}>
        {/* Bar chart */}
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} margin={{ top: 18, right: 10, left: 0, bottom: 0 }} barSize={Math.max(14, Math.min(28, Math.floor(500 / Math.max(años.length, 1)) - 12))} barGap={3} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="año" tick={{ fontSize: '0.68rem', fill: '#6b7280' }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={fmtY} tick={{ fontSize: '0.6rem', fill: '#9ca3af' }} axisLine={false} tickLine={false} width={52} />
            <Tooltip
              formatter={(v, n) => {
                if (n === 'Objetivo') return [`${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)} €`, 'Objetivo'];
                return [`${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)} €`, n];
              }}
              contentStyle={{ fontSize: '0.72rem', borderRadius: 6, border: '1px solid #e5e7eb', padding: '6px 10px' }}
              labelStyle={{ fontWeight: '700', color: '#111827', marginBottom: 4 }}
            />
            {visibleCategories.map(({ cat, origIdx }, vi) => (
              <Bar
                key={cat} dataKey={cat} stackId="a"
                fill={view === 'total' ? DOMAIN_FACTURACION.primary : FACTURACION_PALETTE[origIdx % FACTURACION_PALETTE.length]}
                radius={vi === visibleCategories.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
              >
                {view === 'total' && (
                  <LabelList dataKey={cat} position="top" formatter={(v) => v > 0 ? fmt(v) : ''} style={{ fontSize: '0.58rem', fill: '#374151', fontWeight: '700' }} />
                )}
                {view !== 'total' && (
                  <LabelList dataKey={cat} content={(props) => {
                    const { x, y, width, height, value, index } = props;
                    const total = totalPerYearIndex[index] || 0;
                    if (!value || !total || height < 13) return null;
                    const p = Math.round((value / total) * 100);
                    if (p < 5) return null;
                    return (
                      <text x={x + width + 4} y={y + height / 2} textAnchor="start" dominantBaseline="central"
                        fill="#374151" style={{ fontSize: '0.55rem', fontWeight: '700', pointerEvents: 'none' }}>
                        {p}%
                      </text>
                    );
                  }} />
                )}
              </Bar>
            ))}
            {/* Objetivo bar — only in total view */}
            {view === 'total' && (
              <Bar
                key="Objetivo" dataKey="Objetivo"
                fill="#e2e8f0"
                stroke="#94a3b8"
                strokeWidth={1}
                radius={[3, 3, 0, 0]}
              >
                <LabelList dataKey="Objetivo" position="top" formatter={(v) => v > 0 ? fmt(v) : ''} style={{ fontSize: '0.58rem', fill: '#64748b', fontWeight: '600' }} />
              </Bar>
            )}
          </BarChart>
        </ResponsiveContainer>

        {/* Achievement badges row (view=total only) */}
        {view === 'total' && Object.keys(cumplimiento_por_año).length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {años.filter(a => cumplimiento_por_año[a] !== undefined).map(año => {
              const delta = cumplimiento_por_año[año];
              const achieved = delta >= 0;
              const color = achieved ? '#2d9368' : delta >= -25 ? '#e09c30' : '#e05070';
              const bg = achieved ? '#ecfdf5' : delta >= -25 ? '#fffbeb' : '#fff1f5';
              const sign = delta >= 0 ? '+' : '';
              return (
                <div key={año} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '3px 10px', borderRadius: 20,
                  backgroundColor: bg,
                  border: `1px solid ${color}33`,
                  fontSize: '0.7rem', fontWeight: '700', color,
                }}>
                  <span style={{ color: '#6b7280', fontWeight: '500' }}>{año}</span>
                  {sign}{delta}%
                </div>
              );
            })}
          </div>
        )}

        {/* Legend for breakdown views — click to filter */}
        {view !== 'total' && categories.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
            {categories.map((cat, i) => {
              const isActive = activeCategories.size === 0 || activeCategories.has(cat);
              return (
                <div
                  key={cat}
                  onClick={() => toggleCategory(cat)}
                  title={isActive ? `Filtrar por ${cat}` : `Añadir ${cat} al filtro`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    cursor: 'pointer', opacity: isActive ? 1 : 0.35,
                    transition: 'opacity 0.15s, background 0.15s',
                    padding: '2px 7px', borderRadius: 5,
                    backgroundColor: activeCategories.has(cat) ? FACTURACION_PALETTE[i % FACTURACION_PALETTE.length] + '18' : 'transparent',
                    border: `1px solid ${activeCategories.has(cat) ? FACTURACION_PALETTE[i % FACTURACION_PALETTE.length] + '55' : 'transparent'}`,
                    userSelect: 'none',
                  }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: FACTURACION_PALETTE[i % FACTURACION_PALETTE.length], flexShrink: 0 }} />
                  <span style={{ fontSize: '0.65rem', color: isActive ? '#4b5563' : '#9ca3af', textDecoration: isActive ? 'none' : 'line-through' }}>{cat}</span>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
};

// ─── Module-level cache (persists across navigation) ─────────────────────────
let _homeCache    = { stats: null, kpisNeg: null, kpisArea: null, kpisPresupuesto: null, kpisFacturacion: null, lastUpdate: null };
let _homeCacheHist = { stats: null, kpisNeg: null, kpisArea: null, kpisPresupuesto: null, kpisFacturacion: null, lastUpdate: null };

// ─── Main Component ────────────────────────────────────────────────────────────
const Home = () => {
  const { user } = useAuth();
  const isManager = user?.can_view_pedidos === true;
  const [modoHistorico, setModoHistorico] = useState(false);
  const [stats, setStats]         = useState(_homeCache.stats);
  const [kpisNeg, setKpisNeg]     = useState(_homeCache.kpisNeg);
  const [kpisArea, setKpisArea]   = useState(_homeCache.kpisArea);
  const [kpisPresupuesto, setKpisPresupuesto] = useState(_homeCache.kpisPresupuesto);
  const [kpisFacturacion, setKpisFacturacion] = useState(_homeCache.kpisFacturacion);
  const [loading, setLoading]     = useState(!_homeCache.stats);
  const [loadingHist, setLoadingHist] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]         = useState(null);
  const [lastUpdate, setLastUpdate] = useState(_homeCache.lastUpdate);
  const [operTab, setOperTab]     = useState('iniciativa');
  const [innoTab, setInnoTab]     = useState('innocard');
  const navigate = useNavigate();

  const loadData = async (silent = false, historico = false) => {
    const cache = historico ? _homeCacheHist : _homeCache;
    try {
      if (silent) setRefreshing(true);
      else if (historico) setLoadingHist(true);
      else setLoading(true);
      setError(null);
      const params = historico ? { historico: 'true' } : {};
      const promises = [
        estadisticasAPI.getGenerales(params),
        iniciativasAPI.getKpisDesarrolloNegocio(),
        estadisticasAPI.getPorArea(params),
        isManager ? pedidosAPI.getEstadisticas() : Promise.resolve({ data: null }),
        hitosFacturacionAPI.getHistoricoFacturacion(),
      ];
      const [sRes, kRes, aRes, pRes, fRes] = await Promise.all(promises);
      const ts = new Date();
      const payload = { stats: sRes.data, kpisNeg: kRes.data, kpisArea: aRes.data, kpisPresupuesto: pRes.data, kpisFacturacion: fRes.data, lastUpdate: ts };
      if (historico) _homeCacheHist = payload;
      else _homeCache = payload;
      setStats(sRes.data);
      setKpisNeg(kRes.data);
      setKpisArea(aRes.data);
      setKpisPresupuesto(pRes.data);
      setKpisFacturacion(fRes.data);
      setLastUpdate(ts);
    } catch (err) {
      setError('No se pudieron cargar los datos. Comprueba la conexión.');
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingHist(false);
      setRefreshing(false);
    }
  };

  const toggleHistorico = () => {
    const next = !modoHistorico;
    setModoHistorico(next);
    const cache = next ? _homeCacheHist : _homeCache;
    if (cache.stats) {
      setStats(cache.stats);
      setKpisNeg(cache.kpisNeg);
      setKpisArea(cache.kpisArea);
      setKpisPresupuesto(cache.kpisPresupuesto);
      setKpisFacturacion(cache.kpisFacturacion);
      setLastUpdate(cache.lastUpdate);
    } else {
      loadData(false, next);
    }
  };

  useEffect(() => {
    if (!_homeCache.stats) loadData(false, false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload historico after objectives are updated by Manager
  useEffect(() => {
    const handler = () => {
      _homeCacheHist.kpisFacturacion = null;
      _homeCache.kpisFacturacion = null;
      loadData(true, modoHistorico);
    };
    window.addEventListener('objetivos-updated', handler);
    return () => window.removeEventListener('objetivos-updated', handler);
  }, [modoHistorico]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Loading & Error states ────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid #e5e7eb', borderTopColor: '#4878d0', animation: 'spin 0.8s linear infinite' }} />
      <div style={{ fontSize: '0.82rem', color: '#9ca3af' }}>Cargando dashboard…</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ padding: 24 }}>
      <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '18px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '0.88rem', color: '#dc2626', marginBottom: 10 }}>{error}</div>
        <button onClick={() => loadData()} style={{ padding: '6px 18px', fontSize: '0.8rem', background: '#4878d0', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
          Reintentar
        </button>
      </div>
    </div>
  );

  // ── Data Extraction ───────────────────────────────────────────────────────────
  const {
    desglose_oportunidades:      dOp   = {},
    desglose_proyectos:          dProy = {},
    desglose_innovacion:         dInno = {},
    desglose_oportunidades_costes: cOp  = {},
    desglose_proyectos_costes:   cProy = {},
    desglose_innovacion_costes:  cInno = {},
    desglose_por_tipo:           dPorTipo = {},
    informacion_financiera:      finInfo = {},
    innovacion:                  innoFull = {},
    numero_total_iniciativas:    totalIniciativasAll = 0,
    año_filtro,
  } = stats || {};

  // Pipeline totals (only "live" states, excluding rejected)
  const totalOportLive = (dOp.identificadas || 0) + (dOp.en_propuesta || 0) +
    (dOp.validacion_tecnica || 0) + (dOp.pdte_aprobacion || 0);
  const totalOportAll  = totalOportLive + (dOp.aprobadas || 0) + (dOp.rechazadas || 0) + (dOp.stand_by || 0);

  const _allTipos = Object.values(dPorTipo);
  const totalOperaActive = _allTipos.reduce((s, t) =>
    s + (t.por_planificar || 0) + (t.planificadas || 0) +
    (t.en_curso_no_desviadas || 0) + (t.en_curso_desviadas || 0) + (t.bloqueadas || 0), 0);
  const totalOperaAll = totalOperaActive + _allTipos.reduce((s, t) =>
    s + (t.finalizadas || 0) + (t.canceladas || 0), 0);

  const totalInnoAll = (innoFull?.num_innocards || 0) + (innoFull?.num_innotrans || 0);

  // En-curso decomposed (desviadas = subset of en_curso)
  const innocards_ok  = (dInno.innocards_en_curso || 0) - (dInno.innocards_desviadas || 0);
  const innotrans_ok  = (dInno.innotrans_en_curso || 0) - (dInno.innotrans_desviadas || 0);

  // Per-type cost breakdown (computed from the iniciativas list in innoFull)
  const _icIniciativas = (innoFull?.iniciativas || []).filter(i => i.tipo_innovacion === 'innocard');
  const _itIniciativas = (innoFull?.iniciativas || []).filter(i => i.tipo_innovacion === 'innotrans');
  const icCosteOferta        = _icIniciativas.reduce((s, i) => s + (i.coste_total_estimado  || 0), 0);
  const icCosteReal          = _icIniciativas.reduce((s, i) => s + (i.coste_total_incurrido || 0), 0);
  const icCosteOfertaEnCurso = _icIniciativas.filter(i => i.estado === 'en_curso')   .reduce((s, i) => s + (i.coste_total_estimado || 0), 0);
  const icCosteOfertaDesv    = dInno.innocards_desviadas_coste  || 0;
  const icCosteOfertaFin     = dInno.innocards_finalizadas_coste || _icIniciativas.filter(i => i.estado === 'finalizada') .reduce((s, i) => s + (i.coste_total_estimado || 0), 0);
  const itCosteOferta        = _itIniciativas.reduce((s, i) => s + (i.coste_total_estimado  || 0), 0);
  const itCosteReal          = _itIniciativas.reduce((s, i) => s + (i.coste_total_incurrido || 0), 0);
  const itCosteOfertaEnCurso = _itIniciativas.filter(i => i.estado === 'en_curso')   .reduce((s, i) => s + (i.coste_total_estimado || 0), 0);
  const itCosteOfertaDesv    = dInno.innotrans_desviadas_coste  || 0;
  const itCosteOfertaFin     = dInno.innotrans_finalizadas_coste || _itIniciativas.filter(i => i.estado === 'finalizada') .reduce((s, i) => s + (i.coste_total_estimado || 0), 0);

  // Ratio innovación / total
  const ratioInno = totalIniciativasAll > 0
    ? Math.round(((dInno.innocards_en_curso || 0) + (dInno.innocards_finalizadas || 0) +
        (dInno.innotrans_en_curso || 0) + (dInno.innotrans_finalizadas || 0)) / totalIniciativasAll * 100)
    : 0;

  // Per-tab data (Panel 2 tabs: iniciativa / mantenimiento / servicio)
  const dTab  = dPorTipo[operTab] || {};
  const cTab  = dTab.costes || {};
  const enCursoTab  = (dTab.en_curso_no_desviadas || 0) + (dTab.en_curso_desviadas || 0);
  const totalTabAll = (dTab.por_planificar || 0) + (dTab.planificadas || 0) +
    (dTab.en_curso_no_desviadas || 0) + (dTab.en_curso_desviadas || 0) +
    (dTab.bloqueadas || 0) + (dTab.finalizadas || 0) + (dTab.canceladas || 0);

  // Donut datasets
  const donutOp = [
    { name: 'Identificadas',  value: dOp.identificadas     || 0, color: STATE_C.oportunidades.identificadas      },
    { name: 'En preparación', value: dOp.en_propuesta       || 0, color: STATE_C.oportunidades.en_propuesta       },
    { name: 'Supervisión',    value: dOp.validacion_tecnica || 0, color: STATE_C.oportunidades.validacion_tecnica },
    { name: 'Entregada',      value: dOp.pdte_aprobacion    || 0, color: STATE_C.oportunidades.pdte_aprobacion    },
    { name: 'Aprobadas',      value: dOp.aprobadas          || 0, color: STATE_C.oportunidades.aprobadas          },
    { name: 'Rechazadas',     value: dOp.rechazadas         || 0, color: STATE_C.oportunidades.rechazadas         },
  ].filter(d => d.value > 0);
  const donutOpColors = donutOp.map(d => d.color);

  const donutProy = [
    { name: 'Por planificar', value: dProy.por_planificar        || 0, color: STATE_C.operaciones.por_planificar        },
    { name: 'Planificadas',   value: dProy.planificadas          || 0, color: STATE_C.operaciones.planificadas          },
    { name: 'En curso ✓',     value: dProy.en_curso_no_desviadas || 0, color: STATE_C.operaciones.en_curso_no_desviadas },
    { name: 'Desviadas',      value: dProy.en_curso_desviadas   || 0, color: STATE_C.operaciones.en_curso_desviadas    },
    { name: 'Bloqueadas',     value: dProy.bloqueadas            || 0, color: STATE_C.operaciones.bloqueadas            },
    { name: 'Finalizadas',    value: dProy.finalizadas           || 0, color: STATE_C.operaciones.finalizadas           },
  ].filter(d => d.value > 0);
  const donutProyColors = donutProy.map(d => d.color);

  // En-curso desglosado para donut y row
  const okTabCount    = Math.max(0, enCursoTab - (dTab.desviadas_grave || 0) - (dTab.desviadas_medio || 0));
  const medioTabCount = dTab.desviadas_medio || 0;
  const graveTabCount = dTab.desviadas_grave || 0;
  const enCursoDonutTotal = okTabCount + medioTabCount + graveTabCount;

  const donutTab = [
    { name: 'Por planificar', value: dTab.por_planificar || 0,  color: STATE_C.operaciones.por_planificar        },
    { name: 'Planificadas',   value: dTab.planificadas   || 0,  color: STATE_C.operaciones.planificadas          },
    { name: 'En curso ✓',     value: okTabCount,                color: '#6ed4b0'                                 },
    { name: 'Desv. Medio',    value: medioTabCount,             color: '#f5c96e'                                 },
    { name: 'Desv. Grave',    value: graveTabCount,             color: '#f49a9a'                                 },
    { name: 'Bloqueadas',     value: dTab.bloqueadas     || 0,  color: STATE_C.operaciones.bloqueadas            },
    { name: 'Finalizadas',    value: dTab.finalizadas    || 0,  color: STATE_C.operaciones.finalizadas           },
  ].filter(d => d.value > 0);
  const donutTabColors = donutTab.map(d => d.color);

  const donutInno = [
    { name: 'IC ✓',     value: innocards_ok,                     color: '#a882d8' },
    { name: 'IC desv.', value: dInno.innocards_desviadas  || 0,  color: '#f5c96e' },
    { name: 'IC fin.',  value: dInno.innocards_finalizadas || 0, color: '#d8d3fa' },
    { name: 'IT ✓',     value: innotrans_ok,                     color: '#c5a6f6' },
    { name: 'IT desv.', value: dInno.innotrans_desviadas  || 0,  color: '#fbb690' },
    { name: 'IT fin.',  value: dInno.innotrans_finalizadas || 0, color: '#ece8fe' },
  ].filter(d => d.value > 0);
  const donutInnoColors = donutInno.map(d => d.color);

  // Per-tab donuts for the redesigned Innovación panel
  const totalIcAll = _icIniciativas.length;
  const totalItAll = _itIniciativas.length;

  const donutIc = [
    { name: 'En curso ✓', value: innocards_ok,                    color: '#a882d8' },
    { name: 'Desviadas',  value: dInno.innocards_desviadas  || 0, color: '#f5c96e' },
    { name: 'Finalizadas',value: dInno.innocards_finalizadas || 0, color: '#d8d3fa' },
  ].filter(d => d.value > 0);
  const donutIcColors = donutIc.map(d => d.color);

  const donutIt = [
    { name: 'En curso ✓', value: innotrans_ok,                    color: '#c5a6f6' },
    { name: 'Desviadas',  value: dInno.innotrans_desviadas  || 0, color: '#fbb690' },
    { name: 'Finalizadas',value: dInno.innotrans_finalizadas || 0, color: '#ece8fe' },
  ].filter(d => d.value > 0);
  const donutItColors = donutIt.map(d => d.color);

  const enCurso = _allTipos.reduce((s, t) =>
    s + (t.en_curso_no_desviadas || 0) + (t.en_curso_desviadas || 0), 0);

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="dashboard-root">
      <style>{`.recharts-wrapper svg { overflow: visible !important; }`}</style>

      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#0f172a', margin: 0, letterSpacing: '-0.025em' }}>
            Dashboard Estratégico
          </h1>
          <div style={{ fontSize: '0.72rem', color: '#aab6c7', marginTop: 2 }}>
            {modoHistorico ? 'Vista histórica (todos los años)' : (año_filtro && `Año ${año_filtro}`)}
            {lastUpdate && ` · Actualizado ${lastUpdate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Toggle histórico */}
          <button
            onClick={toggleHistorico}
            disabled={loadingHist}
            title={modoHistorico ? 'Volver al año en curso' : 'Ver datos históricos (todos los años)'}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '4px 12px', fontSize: '0.7rem', fontWeight: '600',
              color: modoHistorico ? '#8654cc' : '#6b7280',
              backgroundColor: modoHistorico ? '#f5f3ff' : '#f8fafc',
              border: `1px solid ${modoHistorico ? '#a47ef455' : '#e2e8f0'}`,
              borderRadius: 20, cursor: loadingHist ? 'default' : 'pointer',
              transition: 'all 0.2s',
              opacity: loadingHist ? 0.6 : 1,
            }}
          >
            {loadingHist ? (
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', border: '2px solid #e5e7eb', borderTopColor: '#8654cc', animation: 'spin 0.8s linear infinite' }} />
            ) : (
              <span style={{ fontSize: '0.8rem' }}>📅</span>
            )}
            {modoHistorico ? 'Histórico activo' : 'Ver histórico'}
          </button>
          {/* Recargar */}
          <button
            onClick={() => loadData(true, modoHistorico)}
            title="Recargar datos"
            disabled={refreshing}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 28, height: 28, flexShrink: 0,
              background: 'transparent',
              color: refreshing ? '#4878d0' : '#c0c8d4',
              border: '1px solid ' + (refreshing ? '#bfdbfe' : '#e5e7eb'),
              borderRadius: '50%', cursor: refreshing ? 'default' : 'pointer',
              fontSize: '0.95rem', lineHeight: 1,
              transition: 'color 0.2s, border-color 0.2s',
              animation: refreshing ? 'spin 0.8s linear infinite' : 'none',
            }}
            onMouseEnter={e => { if (!refreshing) { e.currentTarget.style.color = '#4878d0'; e.currentTarget.style.borderColor = '#93c5fd'; }}}
            onMouseLeave={e => { if (!refreshing) { e.currentTarget.style.color = '#c0c8d4'; e.currentTarget.style.borderColor = '#e5e7eb'; }}}
          >
            ↻
          </button>
        </div>
      </div>

      {/* ── Summary ribbon ──────────────────────────────────────────────── */}
      <div className="dashboard-ribbon">

        {/* Oportunidades trabajadas */}
        <div style={{ background: DOMAIN.oportunidades.soft, borderRadius: 10, border: `1px solid ${DOMAIN.oportunidades.primary}22`, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', lineHeight: 1, gap: 1 }}>
            <div style={{ fontSize: '2.2rem', fontWeight: '900', color: DOMAIN.oportunidades.primary, letterSpacing: '-0.04em', lineHeight: 1 }}>
              <AnimatedNum value={totalOportLive} />
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '700', color: DOMAIN.oportunidades.primary, opacity: 0.45, marginBottom: '0.18rem', whiteSpace: 'nowrap' }}>
              /{totalOportAll}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.78rem', fontWeight: '600', color: '#1e293b' }}>Oportunidades activas</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 6px', marginTop: 4 }}>
              <span style={{ fontSize: '0.68rem', color: '#64748b', whiteSpace: 'nowrap' }}>Pendiente de facturar:</span>
              {[
                { label: 'Alta',  cost: cOp.pdte_aprobacion_alta,     count: dOp.pdte_aprobacion_alta    || 0, color: '#3dc896' },
                { label: 'Media', cost: cOp.pdte_aprobacion_media,    count: dOp.pdte_aprobacion_media   || 0, color: '#f7b73a' },
                { label: 'Baja',  cost: cOp.pdte_aprobacion_baja,     count: dOp.pdte_aprobacion_baja    || 0, color: '#faa0a0' },
                { label: '—',     cost: cOp.pdte_aprobacion_sin_prob, count: dOp.pdte_aprobacion_sin_prob || 0, color: '#aab6c7' },
              ].filter(p => p.count > 0).map(({ label, cost, color }) => (
                <span key={label} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  padding: '1px 6px', borderRadius: 8,
                  backgroundColor: color + '18', border: `1px solid ${color}44`,
                  fontSize: '0.62rem', fontWeight: '700', color, whiteSpace: 'nowrap',
                }}>
                  <span style={{ opacity: 0.75, fontWeight: '500' }}>{label}</span>
                  {cost > 0 ? fmt(cost) : '—'}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Operaciones activas */}
        <div style={{ background: DOMAIN.operaciones.soft, borderRadius: 10, border: `1px solid ${DOMAIN.operaciones.primary}22`, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', lineHeight: 1, gap: 1 }}>
            <div style={{ fontSize: '2.2rem', fontWeight: '900', color: DOMAIN.operaciones.primary, letterSpacing: '-0.04em', lineHeight: 1 }}>
              <AnimatedNum value={enCurso} />
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '700', color: DOMAIN.operaciones.primary, opacity: 0.45, marginBottom: '0.18rem', whiteSpace: 'nowrap' }}>
              /{totalOperaAll}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.78rem', fontWeight: '600', color: '#1e293b' }}>Operaciones en curso</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 6px', marginTop: 4 }}>
              {[
                { label: 'Iniciativa',    key: 'iniciativa',    color: '#3dc896' },
                { label: 'Mantenimiento', key: 'mantenimiento', color: '#87bcfb' },
                { label: 'Servicio',      key: 'servicio',      color: '#b99af8' },
              ].map(({ label, key, color }) => {
                const t = dPorTipo[key] || {};
                const enCursoTipo = (t.en_curso_no_desviadas || 0) + (t.en_curso_desviadas || 0);
                const totalTipo   = (t.por_planificar || 0) + (t.planificadas || 0) + enCursoTipo +
                                    (t.bloqueadas || 0) + (t.finalizadas || 0) + (t.canceladas || 0);
                if (enCursoTipo === 0 && totalTipo === 0) return null;
                return (
                  <span key={label} style={{
                    display: 'inline-flex', alignItems: 'baseline', gap: 2,
                    padding: '1px 6px', borderRadius: 8,
                    backgroundColor: color + '18', border: `1px solid ${color}44`,
                    fontSize: '0.62rem', fontWeight: '700', color, whiteSpace: 'nowrap',
                  }}>
                    <span style={{ opacity: 0.75, fontWeight: '500' }}>{label}</span>
                    {enCursoTipo}
                    <span style={{ fontSize: '0.54rem', fontWeight: '600', opacity: 0.5 }}>/{totalTipo}</span>
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* Iniciativas innovación */}
        <div style={{ background: DOMAIN.innovacion.soft, borderRadius: 10, border: `1px solid ${DOMAIN.innovacion.primary}22`, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', lineHeight: 1, gap: 1 }}>
            <div style={{ fontSize: '2.2rem', fontWeight: '900', color: DOMAIN.innovacion.primary, letterSpacing: '-0.04em', lineHeight: 1 }}>
              <AnimatedNum value={(dInno.innocards_en_curso || 0) + (dInno.innotrans_en_curso || 0)} />
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '700', color: DOMAIN.innovacion.primary, opacity: 0.45, marginBottom: '0.18rem', whiteSpace: 'nowrap' }}>
              /{totalInnoAll}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.78rem', fontWeight: '600', color: '#1e293b' }}>Iniciativas innovación en curso</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 6px', marginTop: 4 }}>
              {[
                { label: 'Coste real',   cost: innoFull?.coste_total_incurrido, color: '#f7b73a' },
                { label: 'Coste oferta', cost: innoFull?.coste_total_estimado,  color: DOMAIN.innovacion.mid },
              ].filter(p => p.cost > 0).map(({ label, cost, color }) => (
                <span key={label} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  padding: '1px 6px', borderRadius: 8,
                  backgroundColor: color + '18', border: `1px solid ${color}44`,
                  fontSize: '0.62rem', fontWeight: '700', color, whiteSpace: 'nowrap',
                }}>
                  <span style={{ opacity: 0.75, fontWeight: '500' }}>{label}</span>
                  {fmt(cost)}
                </span>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* ── Three Strategic Panels ──────────────────────────────────────── */}
      <div className="dashboard-panels">

        {/* ══ Panel 1 · Oportunidades (Desarrollo de Negocio) ══════════ */}
        <Panel domain={DOMAIN.oportunidades} onClick={() => navigate(`/desarrollo-negocio${año_filtro ? `?años=${año_filtro}` : ''}`)}>
          <PanelHeader
            icon="💼" title="Oportunidades" subtitle="Desarrollo de Negocio"
            total={totalOportAll} domain={DOMAIN.oportunidades}
            hideTotal
          />

          <div style={{ padding: '0 20px 14px', display: 'flex', flexDirection: 'column', flex: 1 }}>

            {/* ── KPIs clave · En preparación / Entregada / Aprobadas ── */}
            {(() => {
              const yp = año_filtro ? `&años=${año_filtro}` : '';
              return (
                <div style={{ display: 'flex', gap: 8, paddingBottom: 12, marginBottom: 10, borderBottom: '1px solid #f1f5f9' }}>
                  <FooterKpi
                    value={dOp.en_propuesta ?? 0}
                    subValue={fmt(cOp.en_propuesta)}
                    label="En preparación"
                    color="#6ea5f7"
                    labelTop
                    onClick={e => { e.stopPropagation(); navigate(`/desarrollo-negocio?estados=en_preparacion_propuesta${yp}`); }}
                  />
                  <FooterKpi
                    value={dOp.pdte_aprobacion ?? 0}
                    subValue={fmt(cOp.pdte_aprobacion)}
                    label="Entregada"
                    color="#faa168"
                    labelTop
                    pills={[
                      { label: 'Alta',  value: dOp.pdte_aprobacion_alta    || 0, color: '#3dc896' },
                      { label: 'Media', value: dOp.pdte_aprobacion_media   || 0, color: '#f7b73a' },
                      { label: 'Baja',  value: dOp.pdte_aprobacion_baja    || 0, color: '#faa0a0' },
                      { label: '—',     value: dOp.pdte_aprobacion_sin_prob || 0, color: '#9ca3af' },
                    ]}
                    onClick={e => { e.stopPropagation(); navigate(`/desarrollo-negocio?estados=entregada${yp}`); }}
                  />
                  <FooterKpi
                    value={dOp.aprobadas ?? 0}
                    subValue={fmt(cOp.aprobadas)}
                    label="Aprobadas"
                    color="#3dc896"
                    labelTop
                    onClick={e => { e.stopPropagation(); navigate(`/desarrollo-negocio?estados=aprobada${yp}`); }}
                  />
                </div>
              );
            })()}

            {/* ── Donut con % + lista de estados ── */}
            {(() => {
              const yp = año_filtro ? `&años=${año_filtro}` : '';
              const nameToEstado = {
                'Identificadas':  'oportunidad_identificada',
                'En preparación': 'en_preparacion_propuesta',
                'Supervisión':    'supervision',
                'Entregada':      'entregada',
                'Aprobadas':      'aprobada',
                'Rechazadas':     'rechazada',
              };
              return (
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {/* State list */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 6, borderBottom: '1px solid #f1f5f9', marginBottom: 4 }}>
                      <span style={{ width: 135, fontSize: '0.63rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: 15 }}>Estado</span>
                      <div style={{ flex: 1 }} />
                      <span style={{ width: 24, textAlign: 'right', fontSize: '0.63rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase' }}>Nº</span>
                      <span style={{ width: 64, textAlign: 'right', fontSize: '0.63rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase' }}>Importe</span>
                    </div>
                    <StateRow label="Identificadas"      count={dOp.identificadas      || 0} cost={cOp.identificadas}      color={STATE_C.oportunidades.identificadas}      total={totalOportAll} onClick={e => { e.stopPropagation(); navigate(`/desarrollo-negocio?estados=oportunidad_identificada${yp}`); }} />
                    <StateRow label="En preparación"     count={dOp.en_propuesta        || 0} cost={cOp.en_propuesta}       color={STATE_C.oportunidades.en_propuesta}       total={totalOportAll} highlight onClick={e => { e.stopPropagation(); navigate(`/desarrollo-negocio?estados=en_preparacion_propuesta${yp}`); }} />
                    <StateRow label="Supervisión"        count={dOp.validacion_tecnica  || 0} cost={cOp.validacion_tecnica} color={STATE_C.oportunidades.validacion_tecnica} total={totalOportAll} onClick={e => { e.stopPropagation(); navigate(`/desarrollo-negocio?estados=supervision${yp}`); }} />
                    <StateRow label="Entregada"          count={dOp.pdte_aprobacion     || 0} cost={cOp.pdte_aprobacion}   color={STATE_C.oportunidades.pdte_aprobacion}    total={totalOportAll} highlight onClick={e => { e.stopPropagation(); navigate(`/desarrollo-negocio?estados=entregada${yp}`); }} />
                    <StateRow label="Aprobadas"          count={dOp.aprobadas           || 0} cost={cOp.aprobadas}         color={STATE_C.oportunidades.aprobadas}          total={totalOportAll} highlight onClick={e => { e.stopPropagation(); navigate(`/desarrollo-negocio?estados=aprobada${yp}`); }} />
                    <div style={{ height: 1, backgroundColor: '#f8fafc', margin: '3px 0' }} />
                    <StateRow label="Rechazadas"         count={dOp.rechazadas          || 0} cost={cOp.rechazadas}        color={STATE_C.oportunidades.rechazadas}         total={totalOportAll} onClick={e => { e.stopPropagation(); navigate(`/desarrollo-negocio?estados=rechazada${yp}`); }} />
                    <StateRow label="Stand-By"           count={dOp.stand_by            || 0} cost={cOp.stand_by}          color={STATE_C.oportunidades.stand_by}           total={totalOportAll} onClick={e => { e.stopPropagation(); navigate(`/desarrollo-negocio?estados=stand_by${yp}`); }} />
                  </div>
                  {/* Donut con % por estado */}
                  <div style={{ flexShrink: 0, paddingTop: 4 }}>
                    <MiniDonut data={donutOp} colors={donutOpColors} size={140} pctLabels
                      centerLabel={{ value: totalOportAll, label: 'total' }}
                      onSliceClick={(entry) => { const est = nameToEstado[entry.name]; if (est) navigate(`/desarrollo-negocio?estados=${est}${yp}`); }}
                    />
                  </div>
                </div>
              );
            })()}

          </div>
        </Panel>

        {/* ══ Panel 2 · Operaciones (Iniciativas) ══════════════════════ */}
        <Panel domain={DOMAIN.operaciones} onClick={() => navigate('/operaciones')}>
          <PanelHeader
            icon="⚙️" title="Proyectos" subtitle="Operaciones"
            total={totalOperaAll} domain={DOMAIN.operaciones}
            hideTotal
            rightContent={
              <div style={{ display: 'flex', gap: 3, padding: 3, backgroundColor: '#f1f5f9', borderRadius: 8 }}>
                {[
                  { key: 'iniciativa',    label: 'Iniciativas'    },
                  { key: 'mantenimiento', label: 'Mantenimientos' },
                  { key: 'servicio',      label: 'Servicios'      },
                ].map(({ key, label }) => (
                  <button key={key}
                    onClick={e => { e.stopPropagation(); setOperTab(key); }}
                    style={{
                      padding: '4px 10px', fontSize: '0.68rem',
                      fontWeight: operTab === key ? '700' : '500',
                      color: operTab === key ? DOMAIN.operaciones.primary : '#6b7280',
                      backgroundColor: operTab === key ? 'white' : 'transparent',
                      border: 'none', borderRadius: 6, cursor: 'pointer',
                      boxShadow: operTab === key ? '0 1px 3px rgba(0,0,0,0.10)' : 'none',
                      transition: 'all 0.15s', whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            }
          />

          <div style={{ padding: '0 20px 14px', display: 'flex', flexDirection: 'column', flex: 1 }}>

            {/* ── KPIs clave · En curso / Desviadas / Finalizadas ── */}
            {(() => {
              const q = `tipos=${operTab}&tipoInnovacion=sin_especificar${año_filtro ? `&años=${año_filtro}` : ''}`;
              return (
                <div style={{ display: 'flex', gap: 8, paddingBottom: 12, marginBottom: 10, borderBottom: '1px solid #f1f5f9' }}>
                  <FooterKpi
                    value={enCursoTab}
                    label="En curso"
                    color="#3dc896"
                    labelTop
                    costePair={{ real: cTab.en_curso_coste_real || 0, venta: cTab.en_curso_coste_oferta || cTab.en_curso_no_desviadas || 0 }}
                    onClick={e => { e.stopPropagation(); navigate(`/operaciones?estados=en_curso&${q}`); }}
                  />
                  <FooterKpi
                    value={dTab.desviadas_medio_grave ?? dTab.en_curso_desviadas ?? 0}
                    label="Desviadas"
                    color="#f7b73a"
                    labelTop
                    costePair={{ real: cTab.desviadas_coste_real || 0, venta: cTab.en_curso_desviadas || 0 }}
                    pills={[
                      { label: 'Grave', value: dTab.desviadas_grave  || 0, color: '#f47070' },
                      { label: 'Medio', value: dTab.desviadas_medio  || 0, color: '#f7b73a' },
                    ]}
                    onClick={e => { e.stopPropagation(); navigate(`/operaciones?estados=en_curso&desvio=medio,grave&${q}`); }}
                  />
                  <FooterKpi
                    value={dTab.finalizadas ?? 0}
                    label="Finalizadas"
                    color="#6b7280"
                    labelTop
                    costePair={{ real: cTab.finalizadas_coste_real || 0, venta: cTab.finalizadas || 0 }}
                    onClick={e => { e.stopPropagation(); navigate(`/operaciones?estados=finalizada&${q}`); }}
                  />
                </div>
              );
            })()}

            {/* ── Donut con % + lista de estados ── */}
            {(() => {
              const q = `tipos=${operTab}&tipoInnovacion=sin_especificar${año_filtro ? `&años=${año_filtro}` : ''}`;
              // nameToQuery: slice name → query string (estados + optional desvio)
              const nameToQuery = {
                'Por planificar': `estados=aprobado`,
                'Planificadas':   `estados=planificada`,
                'En curso ✓':     `estados=en_curso&desvio=sin_desvios`,
                'Desv. Medio':    `estados=en_curso&desvio=medio`,
                'Desv. Grave':    `estados=en_curso&desvio=grave`,
                'Bloqueadas':     `estados=bloqueada`,
                'Finalizadas':    `estados=finalizada`,
              };
              return (
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {/* State list */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 6, borderBottom: '1px solid #f1f5f9', marginBottom: 4 }}>
                      <span style={{ width: 135, fontSize: '0.63rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: 15 }}>Estado</span>
                      <div style={{ flex: 1 }} />
                      <span style={{ width: 24, textAlign: 'right', fontSize: '0.63rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase' }}>Nº</span>
                      <span style={{ width: 64, textAlign: 'right', fontSize: '0.63rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase' }}>Importe</span>
                    </div>
                    <StateRow label="Por planificar" count={dTab.por_planificar || 0} cost={cTab.por_planificar} color={STATE_C.operaciones.por_planificar} total={totalTabAll} onClick={e => { e.stopPropagation(); navigate(`/operaciones?estados=aprobado&${q}`); }} />
                    <StateRow label="Planificadas"   count={dTab.planificadas   || 0} cost={cTab.planificadas}   color={STATE_C.operaciones.planificadas}   total={totalTabAll} onClick={e => { e.stopPropagation(); navigate(`/operaciones?estados=planificada&${q}`); }} />
                    <EnCursoRow
                      ok={okTabCount} medio={medioTabCount} grave={graveTabCount}
                      costTotal={cTab.en_curso_no_desviadas}
                      total={totalTabAll}
                      onClick={e => { e.stopPropagation(); navigate(`/operaciones?estados=en_curso&${q}`); }}
                    />
                    <StateRow label="Bloqueadas" count={dTab.bloqueadas || 0} cost={cTab.bloqueadas} color={STATE_C.operaciones.bloqueadas} total={totalTabAll} onClick={e => { e.stopPropagation(); navigate(`/operaciones?estados=bloqueada&${q}`); }} />
                    <div style={{ height: 1, backgroundColor: '#f8fafc', margin: '3px 0' }} />
                    <StateRow label="Finalizadas" count={dTab.finalizadas || 0} cost={cTab.finalizadas} color={STATE_C.operaciones.finalizadas} total={totalTabAll} highlight onClick={e => { e.stopPropagation(); navigate(`/operaciones?estados=finalizada&${q}`); }} />
                    <StateRow label="Canceladas"  count={dTab.canceladas  || 0} cost={cTab.canceladas}  color={STATE_C.operaciones.canceladas}  total={totalTabAll} onClick={e => { e.stopPropagation(); navigate(`/operaciones?estados=cancelada&${q}`); }} />
                  </div>
                  {/* Donut con % por estado (3 segmentos en-curso) */}
                  <div style={{ flexShrink: 0, paddingTop: 4 }}>
                    <MiniDonut data={donutTab} colors={donutTabColors} size={140} pctLabels
                      centerLabel={{ value: totalTabAll, label: 'total' }}
                      onSliceClick={(entry) => { const nq = nameToQuery[entry.name]; if (nq) navigate(`/operaciones?${nq}&${q}`); }}
                    />
                  </div>
                </div>
              );
            })()}

            {/* ── Barra innovación ── */}
            {(() => {
              const inno = dTab.con_innovacion || 0;
              const total = totalTabAll;
              const pctInno = total > 0 ? Math.round((inno / total) * 100) : 0;
              if (total === 0) return null;
              return (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: '0.63rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: '0.7rem' }}>💡</span>
                      Iniciativas innovadoras
                      {inno > 0 && <span style={{ marginLeft: 6, fontSize: '0.65rem', fontWeight: '600', color: '#a47ef4' }}>{pctInno}%</span>}
                    </span>
                    <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#8654cc' }}>
                      {inno} <span style={{ fontWeight: '400', color: '#9ca3af', fontSize: '0.65rem' }}>de {total}</span>                      
                    </span>
                  </div>
                  <div style={{ height: 4, backgroundColor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${pctInno}%`,
                      background: 'linear-gradient(90deg, #8654cc, #a47ef4)',
                      borderRadius: 2, transition: 'width 1s cubic-bezier(0.4,0,0.2,1)',
                    }} />
                  </div>
                </div>
              );
            })()}

          </div>
        </Panel>

        {/* ══ Panel 3 · Innovación ════════════════════════════════════ */}
        <Panel domain={DOMAIN.innovacion} onClick={() => navigate('/innovation')}>
          <PanelHeader
            icon="💡" title="Innovación" subtitle="Innocards · Innotrans"
            total={totalInnoAll} domain={DOMAIN.innovacion}
            hideTotal
            rightContent={
              <div style={{ display: 'flex', gap: 3, padding: 3, backgroundColor: '#f1f5f9', borderRadius: 8 }}>
                {[
                  { key: 'innocard', label: 'Innocards' },
                  { key: 'innotrans', label: 'Innotrans' },
                ].map(({ key, label }) => (
                  <button key={key}
                    onClick={e => { e.stopPropagation(); setInnoTab(key); }}
                    style={{
                      padding: '4px 10px', fontSize: '0.68rem',
                      fontWeight: innoTab === key ? '700' : '500',
                      color: innoTab === key ? DOMAIN.innovacion.primary : '#6b7280',
                      backgroundColor: innoTab === key ? 'white' : 'transparent',
                      border: 'none', borderRadius: 6, cursor: 'pointer',
                      boxShadow: innoTab === key ? '0 1px 3px rgba(0,0,0,0.10)' : 'none',
                      transition: 'all 0.15s', whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            }
          />

          <div style={{ padding: '0 20px 14px', display: 'flex', flexDirection: 'column', flex: 1 }}>

            {innoTab === 'innocard' ? (() => {
              const enCursoIc    = dInno.innocards_en_curso   || 0;
              const desviadasIc  = dInno.innocards_desviadas  || 0;
              const finalizadasIc= dInno.innocards_finalizadas || 0;
              const icGrave      = dInno.innocards_desviadas_grave || 0;
              const icMedio      = dInno.innocards_desviadas_medio || 0;
              return (<>
                {/* ── KPIs ── */}
                <div style={{ display: 'flex', gap: 8, paddingBottom: 12, marginBottom: 10, borderBottom: '1px solid #f1f5f9' }}>
                  <FooterKpi
                    value={enCursoIc}
                    subValue={icCosteOferta > 0 ? fmt(icCosteOferta) : undefined}
                    label="En curso"
                    color="#9660ea"
                    labelTop
                    onClick={e => { e.stopPropagation(); navigate('/innovation?tipo=innocard&estados=en_curso'); }}
                  />
                  <FooterKpi
                    value={desviadasIc}
                    subValue={icCosteOfertaDesv > 0 ? fmt(icCosteOfertaDesv) : undefined}
                    label="Desviadas"
                    color="#f7b73a"
                    labelTop
                    pills={[
                      { label: 'Grave', value: icGrave, color: '#f47070' },
                      { label: 'Medio', value: icMedio, color: '#f7b73a' },
                    ]}
                    onClick={e => { e.stopPropagation(); navigate('/innovation?tipo=innocard&estados=en_curso'); }}
                  />
                  <FooterKpi
                    value={finalizadasIc}
                    subValue={icCosteOfertaFin > 0 ? fmt(icCosteOfertaFin) : undefined}
                    label="Finalizadas"
                    color="#6b7280"
                    labelTop
                    onClick={e => { e.stopPropagation(); navigate('/innovation?tipo=innocard&estados=finalizada'); }}
                  />
                </div>

                {/* ── Coste real vs oferta ── */}
                {(icCosteOferta > 0 || icCosteReal > 0) && (() => {
                  const maxC = Math.max(icCosteOferta, icCosteReal, 1);
                  const pctReal   = Math.min(100, (icCosteReal   / maxC) * 100);
                  const pctOferta = Math.min(100, (icCosteOferta / maxC) * 100);
                  const overrun   = icCosteReal > icCosteOferta && icCosteOferta > 0;
                  return (
                    <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: '0.63rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Coste real vs oferta</span>
                        {overrun && (
                          <span style={{ fontSize: '0.6rem', fontWeight: '700', color: '#f47070', backgroundColor: '#fef2f2', border: '1px solid #fdd8d8', borderRadius: 6, padding: '1px 6px' }}>
                            +{Math.round(((icCosteReal - icCosteOferta) / icCosteOferta) * 100)}% desviación
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 52, fontSize: '0.6rem', color: '#a47ef4', fontWeight: '600', flexShrink: 0 }}>Oferta</span>
                          <div style={{ flex: 1, height: 6, backgroundColor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${pctOferta}%`, height: '100%', backgroundColor: '#a47ef4', borderRadius: 3, transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)' }} />
                          </div>
                          <span style={{ width: 60, textAlign: 'right', fontSize: '0.68rem', fontWeight: '700', color: '#a47ef4', flexShrink: 0 }}>{fmt(icCosteOferta)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 52, fontSize: '0.6rem', color: overrun ? '#f47070' : '#3dc896', fontWeight: '600', flexShrink: 0 }}>Real</span>
                          <div style={{ flex: 1, height: 6, backgroundColor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${pctReal}%`, height: '100%', backgroundColor: overrun ? '#f47070' : '#3dc896', borderRadius: 3, transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)' }} />
                          </div>
                          <span style={{ width: 60, textAlign: 'right', fontSize: '0.68rem', fontWeight: '700', color: overrun ? '#f47070' : '#3dc896', flexShrink: 0 }}>{fmt(icCosteReal)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* ── Lista de estados + Donut ── */}
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 6, borderBottom: '1px solid #f1f5f9', marginBottom: 4 }}>
                      <span style={{ width: 135, fontSize: '0.63rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: 15 }}>Estado</span>
                      <div style={{ flex: 1 }} />
                      <span style={{ width: 24, textAlign: 'right', fontSize: '0.63rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase' }}>Nº</span>
                      <span style={{ width: 64, textAlign: 'right', fontSize: '0.63rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase' }}>Coste est.</span>
                    </div>
                    <EnCursoRow
                      ok={innocards_ok} medio={desviadasIc} grave={0}
                      costTotal={icCosteOfertaEnCurso} total={totalIcAll}
                      onClick={e => { e.stopPropagation(); navigate('/innovation?tipo=innocard&estados=en_curso'); }}
                    />
                    {finalizadasIc > 0 && (
                      <StateRow label="Finalizadas" count={finalizadasIc} cost={icCosteOfertaFin} color={STATE_C.innovacion.innocards_finalizadas} total={totalIcAll} highlight onClick={e => { e.stopPropagation(); navigate('/innovation?tipo=innocard&estados=finalizada'); }} />
                    )}
                  </div>
                  <div style={{ flexShrink: 0, paddingTop: 4 }}>
                    <MiniDonut data={donutIc} colors={donutIcColors} size={130} pctLabels
                      centerLabel={{ value: totalIcAll, label: 'total' }}
                      onSliceClick={() => navigate('/innovation?tipo=innocard')}
                    />
                  </div>
                </div>
              </>);
            })() : (() => {
              const enCursoIt    = dInno.innotrans_en_curso   || 0;
              const desviadasIt  = dInno.innotrans_desviadas  || 0;
              const finalizadasIt= dInno.innotrans_finalizadas || 0;
              const itGrave      = dInno.innotrans_desviadas_grave || 0;
              const itMedio      = dInno.innotrans_desviadas_medio || 0;
              return (<>
                {/* ── KPIs ── */}
                <div style={{ display: 'flex', gap: 8, paddingBottom: 12, marginBottom: 10, borderBottom: '1px solid #f1f5f9' }}>
                  <FooterKpi
                    value={enCursoIt}
                    subValue={itCosteOferta > 0 ? fmt(itCosteOferta) : undefined}
                    label="En curso"
                    color="#bc7af8"
                    labelTop
                    onClick={e => { e.stopPropagation(); navigate('/innovation?tipo=innotrans&estados=en_curso'); }}
                  />
                  <FooterKpi
                    value={desviadasIt}
                    subValue={itCosteOfertaDesv > 0 ? fmt(itCosteOfertaDesv) : undefined}
                    label="Desviadas"
                    color="#faa168"
                    labelTop
                    pills={[
                      { label: 'Grave', value: itGrave, color: '#f47070' },
                      { label: 'Medio', value: itMedio, color: '#f7b73a' },
                    ]}
                    onClick={e => { e.stopPropagation(); navigate('/innovation?tipo=innotrans&estados=en_curso'); }}
                  />
                  <FooterKpi
                    value={finalizadasIt}
                    subValue={itCosteOfertaFin > 0 ? fmt(itCosteOfertaFin) : undefined}
                    label="Finalizadas"
                    color="#6b7280"
                    labelTop
                    onClick={e => { e.stopPropagation(); navigate('/innovation?tipo=innotrans&estados=finalizada'); }}
                  />
                </div>

                {/* ── Coste real vs oferta ── */}
                {(itCosteOferta > 0 || itCosteReal > 0) && (() => {
                  const maxC = Math.max(itCosteOferta, itCosteReal, 1);
                  const pctReal   = Math.min(100, (itCosteReal   / maxC) * 100);
                  const pctOferta = Math.min(100, (itCosteOferta / maxC) * 100);
                  const overrun   = itCosteReal > itCosteOferta && itCosteOferta > 0;
                  return (
                    <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: '0.63rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Coste real vs oferta</span>
                        {overrun && (
                          <span style={{ fontSize: '0.6rem', fontWeight: '700', color: '#f47070', backgroundColor: '#fef2f2', border: '1px solid #fdd8d8', borderRadius: 6, padding: '1px 6px' }}>
                            +{Math.round(((itCosteReal - itCosteOferta) / itCosteOferta) * 100)}% desviación
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 52, fontSize: '0.6rem', color: '#bc7af8', fontWeight: '600', flexShrink: 0 }}>Oferta</span>
                          <div style={{ flex: 1, height: 6, backgroundColor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${pctOferta}%`, height: '100%', backgroundColor: '#bc7af8', borderRadius: 3, transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)' }} />
                          </div>
                          <span style={{ width: 60, textAlign: 'right', fontSize: '0.68rem', fontWeight: '700', color: '#bc7af8', flexShrink: 0 }}>{fmt(itCosteOferta)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 52, fontSize: '0.6rem', color: overrun ? '#f47070' : '#3dc896', fontWeight: '600', flexShrink: 0 }}>Real</span>
                          <div style={{ flex: 1, height: 6, backgroundColor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${pctReal}%`, height: '100%', backgroundColor: overrun ? '#f47070' : '#3dc896', borderRadius: 3, transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)' }} />
                          </div>
                          <span style={{ width: 60, textAlign: 'right', fontSize: '0.68rem', fontWeight: '700', color: overrun ? '#f47070' : '#3dc896', flexShrink: 0 }}>{fmt(itCosteReal)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* ── Lista de estados + Donut ── */}
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 6, borderBottom: '1px solid #f1f5f9', marginBottom: 4 }}>
                      <span style={{ width: 135, fontSize: '0.63rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: 15 }}>Estado</span>
                      <div style={{ flex: 1 }} />
                      <span style={{ width: 24, textAlign: 'right', fontSize: '0.63rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase' }}>Nº</span>
                      <span style={{ width: 64, textAlign: 'right', fontSize: '0.63rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase' }}>Coste est.</span>
                    </div>
                    <EnCursoRow
                      ok={innotrans_ok} medio={desviadasIt} grave={0}
                      costTotal={itCosteOfertaEnCurso} total={totalItAll}
                      onClick={e => { e.stopPropagation(); navigate('/innovation?tipo=innotrans&estados=en_curso'); }}
                    />
                    {finalizadasIt > 0 && (
                      <StateRow label="Finalizadas" count={finalizadasIt} cost={itCosteOfertaFin} color={STATE_C.innovacion.innotrans_finalizadas} total={totalItAll} highlight onClick={e => { e.stopPropagation(); navigate('/innovation?tipo=innotrans&estados=finalizada'); }} />
                    )}
                  </div>
                  <div style={{ flexShrink: 0, paddingTop: 4 }}>
                    <MiniDonut data={donutIt} colors={donutItColors} size={130} pctLabels
                      centerLabel={{ value: totalItAll, label: 'total' }}
                      onSliceClick={() => navigate('/innovation?tipo=innotrans')}
                    />
                  </div>
                </div>
              </>);
            })()}

          </div>
        </Panel>

      </div>{/* end three panels */}

      {/* ── Area Hub Block ──────────────────────────────────────────── */}
      <AreaHubBlock data={kpisArea} />

      {/* ── Presupuesto Block ───────────────────────────────────────── */}
      {isManager && <PresupuestoBlock data={kpisPresupuesto} />}

      {/* ── Facturación Histórico Block ─────────────────────────────── */}
      <FacturacionHistoricoBlock data={kpisFacturacion} isManager={isManager} />

    </div>
  );
};

export default Home;
